/**
 * =============================================================================
 * ALTARA COLOR SYSTEM — Single Source of Truth
 * =============================================================================
 *
 * ALL colors in the app should be imported from this file.
 * Colors reference CSS custom properties defined in index.css.
 *
 * USAGE:
 * - Components: import { COLORS, CHART_COLORS, FUNNEL_COLORS } from "@/lib/colorSystem"
 * - For inline styles: use the CSS var strings directly
 * - For Tailwind: use the ds-* classes (bg-ds-accent, text-ds-green, etc.)
 *
 * DO NOT:
 * - Hardcode hex colors in components
 * - Define color constants in component files
 * - Use raw Tailwind color classes (bg-blue-500) — use ds-* tokens instead
 */

// =============================================================================
// CSS Variable References
// =============================================================================
// These map to :root variables in index.css

export const CSS_VARS = {
  // Background & Surface
  bg: "var(--bg)",
  surfaceSubtle: "var(--surface-subtle)",
  surface: "var(--surface)",
  surfaceHover: "var(--surface-hover)",

  // Borders
  border: "var(--border)",
  borderLight: "var(--border-light)",
  borderFocus: "var(--border-focus)",

  // Text
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textTertiary: "var(--text-tertiary)",

  // Accent
  accent: "var(--accent)",
  accentLight: "var(--accent-light)",
  accentHover: "var(--accent-hover)",

  // Semantic
  green: "var(--green)",
  greenBg: "var(--green-bg)",
  red: "var(--red)",
  redBg: "var(--red-bg)",
  amber: "var(--amber)",
  amberBg: "var(--amber-bg)",
  purple: "var(--purple)",
  purpleBg: "var(--purple-bg)",

  // Chart
  chartPrimary: "var(--chart-primary)",
  chartSecondary: "var(--chart-secondary)",
  chartForecast: "var(--chart-forecast)",
  chartTarget: "var(--chart-target)",
  chartGrid: "var(--chart-grid)",

  // Funnel Sources
  funnelWebinar: "var(--funnel-webinar)",
  funnelTypeform: "var(--funnel-typeform)",
  funnelColddm: "var(--funnel-colddm)",

  // Cohort mode accent
  cohortAccent: "var(--cohort-accent)",
  cohortAccentBg: "var(--cohort-accent-bg)",
  cohortAccentBorder: "var(--cohort-accent-border)",
  cohortAccentGlow: "var(--cohort-accent-glow)",
} as const;

// =============================================================================
// Hex Values (for SVG/Canvas that can't use CSS vars)
// =============================================================================
// These MUST stay in sync with index.css :root
// Only use these when CSS vars are technically impossible (SVG gradients, Canvas)

export const HEX = {
  // Accent — matches CSS var --accent in index.css
  accent: "#3B82F6",
  accentHover: "#2563EB",

  // Semantic
  green: "#10B981",
  red: "#EF4444",
  amber: "#F59E0B",
  purple: "#8B5CF6",

  // Chart (same as semantic for most)
  chartPrimary: "#3B82F6", // Blue
  chartSecondary: "#F59E0B", // Amber
  chartForecast: "#8B5CF6", // Purple
  chartTarget: "#EF4444", // Red

  // Funnel Sources
  funnelWebinar: "#3B82F6",
  funnelTypeform: "#F59E0B",
  funnelColddm: "#8B5CF6",

  // Additional colors used in charts
  cyan: "#06B6D4",
  orange: "#F97316",

  // Grays (for borders, backgrounds)
  gray50: "#F9FAFB",
  gray100: "#F3F4F6",
  gray200: "#E5E7EB",
  gray300: "#D1D5DB",
  gray400: "#9CA3AF",
  gray500: "#6B7280",
  gray600: "#4B5563",
  gray700: "#374151",
  gray800: "#1F2937",
  gray900: "#111827",

  // Surface
  white: "#FFFFFF",
  black: "#000000",
} as const;

// =============================================================================
// Funnel Colors
// =============================================================================

export type FunnelSource = "webinar" | "typeform" | "dm";

export const FUNNEL_COLORS: Record<FunnelSource, string> = {
  webinar: CSS_VARS.funnelWebinar,
  typeform: CSS_VARS.funnelTypeform,
  dm: CSS_VARS.funnelColddm,
};

// Hex version for SVG/Canvas contexts
export const FUNNEL_COLORS_HEX: Record<FunnelSource, string> = {
  webinar: HEX.funnelWebinar,
  typeform: HEX.funnelTypeform,
  dm: HEX.funnelColddm,
};

export const LOW_TICKET_COLOR = CSS_VARS.green;
export const LOW_TICKET_COLOR_HEX = HEX.green;

// =============================================================================
// Chart Colors
// =============================================================================

export const CHART_COLORS = {
  // Primary data series
  primary: CSS_VARS.chartPrimary,
  secondary: CSS_VARS.chartSecondary,

  // Special purpose
  forecast: CSS_VARS.chartForecast,
  target: CSS_VARS.chartTarget,
  grid: CSS_VARS.chartGrid,

  // Semantic (for positive/negative indicators)
  positive: CSS_VARS.green,
  negative: CSS_VARS.red,
  warning: CSS_VARS.amber,
} as const;

// Hex versions for Recharts/SVG contexts
export const CHART_COLORS_HEX = {
  primary: HEX.chartPrimary,
  secondary: HEX.chartSecondary,
  forecast: HEX.chartForecast,
  target: HEX.chartTarget,
  positive: HEX.green,
  negative: HEX.red,
  warning: HEX.amber,
  // Cash & Revenue series (hero card + FinancialGraph)
  cash: HEX.chartPrimary,
  revenue: "#E8622C",
  // Additional chart colors
  cyan: HEX.cyan,
  orange: HEX.orange,
  purple: HEX.purple,
} as const;

// =============================================================================
// Outcome Badge Colors
// =============================================================================

export const OUTCOME_COLORS = {
  win: CSS_VARS.green,
  loss: CSS_VARS.red,
  noshow: CSS_VARS.amber,
  installment: CSS_VARS.purple,
} as const;

export const OUTCOME_COLORS_HEX = {
  win: HEX.green,
  loss: HEX.red,
  noshow: HEX.amber,
  installment: HEX.purple,
} as const;

// =============================================================================
// Status Colors (for buckets, badges, indicators)
// =============================================================================

export const STATUS_COLORS = {
  // Time-based buckets (DM analytics)
  fast: CSS_VARS.green, // <30min, <5min
  medium: CSS_VARS.amber, // 30min-1hr, 5-15min
  slow: CSS_VARS.red, // 1hr+, 15min+

  // Engagement levels
  engaged: CSS_VARS.green,
  light: CSS_VARS.amber,
  ghost: CSS_VARS.red,
  deep: CSS_VARS.accent,
} as const;

// =============================================================================
// Stagger/Progress Colors
// =============================================================================

export const PROGRESS_COLORS = {
  ahead: CSS_VARS.green,
  onTrack: CSS_VARS.amber,
  behind: CSS_VARS.red,
} as const;

export const PROGRESS_COLORS_HEX = {
  ahead: HEX.green,
  onTrack: HEX.amber,
  behind: HEX.red,
} as const;

// =============================================================================
// Helper: Get color with opacity
// =============================================================================

/**
 * Convert hex color to rgba with opacity
 * @param hex - Hex color (e.g., "#3B82F6")
 * @param opacity - Opacity 0-1
 */
export function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Get gradient stops for area charts
 * @param color - Hex color for the gradient
 * @param startOpacity - Top opacity (default 0.3)
 * @param endOpacity - Bottom opacity (default 0.02)
 */
export function getGradientStops(
  color: string,
  startOpacity = 0.3,
  endOpacity = 0.02
): { start: string; end: string } {
  return {
    start: hexToRgba(color, startOpacity),
    end: hexToRgba(color, endOpacity),
  };
}
